import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { ContentEditorService } from '../../../core/services/content-editor.service';
import {
  ConditionalError,
  Correct,
  PlayerContentMessage,
  PlayerSettings,
  TaschenrechnerItem,
  TaschenrechnerPart,
} from '../../../core/types/player-types';
import { ExamEntity } from '../../exam/exam.entity';
import { ExerciseEntity, LearnItemConfig } from '../../exercise/exercise.entity';
import {
  createNumberListFrom,
  defaultTokenHtmlAdapter,
  isNumberOrFraction,
  removeTokenFromText,
  replaceTokenSolution,
  TaschenrechnerTokenHtmlAdapter,
} from './taschenrechner-editor.util';

@Component({
  selector: 'pixl-taschenrechner-editor',
  standalone: true,
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    PixlEditorComponent,
    TranslocoModule,
  ],
  templateUrl: './taschenrechner-editor.component.html',
  styleUrl: './taschenrechner-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaschenrechnerEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly part = input.required<TaschenrechnerPart>();
  public readonly exerciseConfig = input<LearnItemConfig | null>(null);
  public readonly exerciseData = input<ExerciseEntity | ExamEntity | null>(null);
  public readonly playerContentMessage = input<PlayerContentMessage | null>(null);
  public readonly subjectId = input<string>('1');
  public readonly isExam = input<boolean>(false);
  public readonly authToken = input<string | null>(null);
  public readonly assetsUrl = input<string>('');
  public readonly mediaUrl = input<string>('');
  public readonly playerSettings = input<PlayerSettings | null>(null);

  public readonly pixlEditor = viewChild<PixlEditorComponent>('pixlEditor');

  public readonly expandedItems = new Set<string>();

  public readonly adapter: TaschenrechnerTokenHtmlAdapter = defaultTokenHtmlAdapter;
  private isEditorInitialized = false;
  private isSyncingFromItems = false;
  private isSyncingFromEditor = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private attachedTipTapEditor: any = null;
  private editorAttachTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly contentEditorService = inject(ContentEditorService);
  private readonly cdr = inject(ChangeDetectorRef);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getTipTapEditor(): any {
    return this.pixlEditor()?.editor?.tipTapEditor;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getEditorHtml(tipTap: any): string {
    return this.adapter.serializeEditorHtml(tipTap);
  }

  public get materialTabName(): string | undefined {
    return this.exerciseConfig()?.materialTabName;
  }

  public set materialTabName(value: string | undefined) {
    const cfg = this.exerciseConfig();
    if (cfg) {
      cfg.materialTabName = value || undefined;
      this.contentEditorService.notifyPartChanged();
    }
  }

  public get correctList(): Correct[] {
    const pcm = this.playerContentMessage();
    if (pcm?.learnItem) {
      if (!pcm.learnItem.correct) {
        pcm.learnItem.correct = [];
      }
      return pcm.learnItem.correct;
    }
    const data = this.exerciseData();
    if (data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyData = data as any;
      if (!anyData.correct) {
        anyData.correct = [];
      }
      return anyData.correct as Correct[];
    }
    return [];
  }

  public get conditionalErrorsList(): ConditionalError[] {
    const data = this.exerciseData();
    if (data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyData = data as any;
      if (!anyData.conditionalErrors) {
        anyData.conditionalErrors = [];
      }
      return anyData.conditionalErrors as ConditionalError[];
    }
    return [];
  }

  public get mathEquality(): Record<string, boolean> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cfg = (this.exerciseConfig() || (this.exerciseData() as any)?.config || {}) as any;
    if (!cfg.mathEquality) {
      cfg.mathEquality = {};
    }
    return cfg.mathEquality as Record<string, boolean>;
  }

  public get ignoredConditions(): Record<string, boolean> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cfg = (this.exerciseConfig() || (this.exerciseData() as any)?.config || {}) as any;
    if (!cfg.ignoredConditions) {
      cfg.ignoredConditions = {};
    }
    return cfg.ignoredConditions as Record<string, boolean>;
  }

  public ngOnInit(): void {
    this.ensureDataMutable();
    this.reconcileExistingTokens();
    this.syncCorrectWithItems();
  }

  private reconcileExistingTokens(): void {
    const rawText = this.part()?.text ?? '';
    if (!rawText) return;

    const tokensFromText = this.adapter.extractTokensFromHtml(rawText);
    if (tokensFromText.length === 0) return;

    if (!Array.isArray(this.part().items)) {
      this.part().items = [];
    }

    const currentItemMap = new Map(this.part().items.map(i => [i.id, i]));
    let itemsAddedOrUpdated = false;

    for (const token of tokensFromText) {
      const existing = currentItemMap.get(token.id);
      if (existing) {
        if (!existing.text && token.solution) {
          existing.text = token.solution;
          itemsAddedOrUpdated = true;
        }
      }
      else {
        const newItem: TaschenrechnerItem = {
          id: token.id,
          text: token.solution,
          kind: 'placeholderEnum',
        };
        this.part().items.push(newItem);
        currentItemMap.set(token.id, newItem);
        itemsAddedOrUpdated = true;
      }
      this.getOrCreateCorrect(token.id, token.solution);
      if (this.isMathSubject() && this.mathEquality[token.id] === undefined) {
        this.mathEquality[token.id] = true;
      }
    }

    if (itemsAddedOrUpdated) {
      this.part().items = [...this.part().items];
      this.syncCorrectToData();
    }
  }

  private ensureDataMutable(): void {
    const pcm = this.playerContentMessage();
    if (pcm?.learnItem) {
      if (!Array.isArray(pcm.learnItem.correct)) {
        pcm.learnItem.correct = [];
      }
      else {
        pcm.learnItem.correct = pcm.learnItem.correct.map(c => ({
          condition: {
            left: c.condition?.left ?? '',
            operation: c.condition?.operation ?? 'in',
            right: c.condition?.right ?? '',
          },
          message: c.message ?? '',
        }));
      }
    }

    const data = this.exerciseData();
    if (data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyData = data as any;
      if (!Array.isArray(anyData.correct)) {
        anyData.correct = [];
      }
      else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        anyData.correct = anyData.correct.map((c: any) => ({
          condition: {
            left: c.condition?.left ?? '',
            operation: c.condition?.operation ?? 'in',
            right: c.condition?.right ?? '',
          },
          message: c.message ?? '',
        }));
      }
      if (anyData.conditionalErrors && Array.isArray(anyData.conditionalErrors)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        anyData.conditionalErrors = anyData.conditionalErrors.map((e: any) => ({
          condition: {
            left: e.condition?.left ?? '',
            operation: e.condition?.operation ?? 'in',
            right: e.condition?.right ?? '',
          },
          message: e.message ?? '',
        }));
      }
      if (anyData.config && typeof anyData.config === 'object') {
        anyData.config = {
          ...anyData.config,
          mathEquality: anyData.config.mathEquality ? { ...anyData.config.mathEquality } : {},
          ignoredConditions: anyData.config.ignoredConditions ? { ...anyData.config.ignoredConditions } : {},
        };
      }
    }

    const p = this.part();
    if (p) {
      if (!Array.isArray(p.items)) {
        p.items = [];
      }
      else {
        p.items = p.items.map(i => ({
          id: i.id,
          text: i.text ?? '',
          kind: i.kind ?? 'placeholderEnum',
        }));
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cfg = (this.exerciseConfig() || (this.exerciseData() as any)?.config) as any;
    if (cfg) {
      if (cfg.mathEquality && typeof cfg.mathEquality === 'object') {
        cfg.mathEquality = { ...cfg.mathEquality };
      }
      else {
        cfg.mathEquality = {};
      }
      if (cfg.ignoredConditions && typeof cfg.ignoredConditions === 'object') {
        cfg.ignoredConditions = { ...cfg.ignoredConditions };
      }
      else {
        cfg.ignoredConditions = {};
      }
    }
  }

  private syncCorrectWithItems(): void {
    const items = this.part()?.items ?? [];
    for (const item of items) {
      this.getOrCreateCorrect(item.id, item.text);
    }
    const itemIds = new Set(items.map(i => i.id));
    for (let i = this.correctList.length - 1; i >= 0; i--) {
      if (!itemIds.has(this.correctList[i].condition?.left)) {
        this.correctList.splice(i, 1);
      }
    }
    this.syncCorrectToData();
  }

  private syncCorrectToData(): void {
    const list = this.correctList;
    const data = this.exerciseData();
    if (data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (data as any).correct = list;
    }
    const pcm = this.playerContentMessage();
    if (pcm?.learnItem) {
      pcm.learnItem.correct = list;
    }
  }

  public ngAfterViewInit(): void {
    this.setupTipTapListener();
  }

  public ngOnDestroy(): void {
    if (this.attachedTipTapEditor) {
      this.attachedTipTapEditor.off('update', this.handleTipTapUpdate);
      this.attachedTipTapEditor = null;
    }
    if (this.editorAttachTimeout) {
      clearTimeout(this.editorAttachTimeout);
      this.editorAttachTimeout = null;
    }
  }

  private readonly handleTipTapUpdate = (): void => {
    if (this.isSyncingFromItems) {
      return;
    }
    this.syncFromEditor();
  };

  private setupTipTapListener(): void {
    const tipTap = this.getTipTapEditor();
    if (tipTap) {
      this.attachedTipTapEditor = tipTap;
      tipTap.on('update', this.handleTipTapUpdate);
    }
    else {
      this.editorAttachTimeout = setTimeout(() => {
        const retryTipTap = this.getTipTapEditor();
        if (retryTipTap && !this.attachedTipTapEditor) {
          this.attachedTipTapEditor = retryTipTap;
          retryTipTap.on('update', this.handleTipTapUpdate);
        }
      }, 50);
    }
  }

  public syncFromHtml(html: string): void {
    if (!html && html !== '') {
      return;
    }
    if (this.isSyncingFromEditor || this.isSyncingFromItems) {
      return;
    }

    const isHtmlEmpty = !html || html === '<p></p>' || html === '<p><br></p>' || html.trim() === '';
    const hasExistingContent = !!this.part().text && this.part().text !== '<p></p>' && this.part().text !== '<p><br></p>';

    // If editor has not yet been marked initialized and reports empty while we have existing content,
    // this is the initial pre-hydration phase — ignore to prevent wiping out data.
    if (!this.isEditorInitialized) {
      const hasExistingTokens = (this.part().items?.length ?? 0) > 0 || (this.part().text?.includes('{id:') ?? false);
      const activeTokensCheck = this.adapter.extractTokensFromHtml(html);
      if ((isHtmlEmpty || (activeTokensCheck.length === 0 && hasExistingTokens)) && hasExistingContent) {
        return;
      }
      this.isEditorInitialized = true;
    }

    this.isSyncingFromEditor = true;

    try {
      const zavText = this.adapter.fromHtmlToZav(html);
      let textChanged = false;
      if (this.part().text !== zavText) {
        this.part().text = zavText;
        textChanged = true;
      }

      // Extract tokens in document order from the HTML
      const activeTokens = this.adapter.extractTokensFromHtml(html);
      const activeIds = new Set(activeTokens.map(t => t.id));
      let itemsChanged = false;

      // 1. Remove items that were deleted in the editor / modal
      const currentItems = this.part().items ?? [];
      const remainingItems = currentItems.filter(item => {
        if (!activeIds.has(item.id)) {
          this.expandedItems.delete(item.id);
          delete this.mathEquality[item.id];
          delete this.ignoredConditions[item.id];
          itemsChanged = true;
          return false;
        }
        return true;
      });

      if (itemsChanged) {
        this.part().items = remainingItems;

        // Clean rules in correctList
        const remainingCorrect = this.correctList.filter(c => c.condition?.left && activeIds.has(c.condition.left));
        if (remainingCorrect.length !== this.correctList.length) {
          this.correctList.length = 0;
          this.correctList.push(...remainingCorrect);
        }

        // Clean conditionalErrors
        const remainingErrors = this.conditionalErrorsList.filter(e => e.condition?.left && activeIds.has(e.condition.left));
        if (remainingErrors.length !== this.conditionalErrorsList.length) {
          this.conditionalErrorsList.length = 0;
          this.conditionalErrorsList.push(...remainingErrors);
        }
      }

      // 2. Update existing items and add new tokens
      const updatedItems = [...this.part().items];
      for (const token of activeTokens) {
        const itemIdx = updatedItems.findIndex(i => i.id === token.id);
        if (itemIdx >= 0) {
          const item = updatedItems[itemIdx];
          if (item.text !== token.solution) {
            updatedItems[itemIdx] = { ...item, text: token.solution };
            itemsChanged = true;
          }
          const rule = this.getOrCreateCorrect(token.id, token.solution);
          if (rule.condition.right !== token.solution) {
            rule.condition.right = token.solution;
            itemsChanged = true;
          }
        }
        else {
          const newItem: TaschenrechnerItem = {
            id: token.id,
            text: token.solution,
            kind: 'placeholderEnum',
          };
          updatedItems.push(newItem);
          this.getOrCreateCorrect(token.id, token.solution);
          if (this.isMathSubject()) {
            this.mathEquality[token.id] = true;
          }
          this.expandedItems.add(token.id);
          itemsChanged = true;
        }
      }
      this.part().items = updatedItems;

      // 3. Keep item ordering in sync with the order in the document
      const tokenOrderMap = new Map(activeTokens.map((t, idx) => [t.id, idx]));
      const sortedItems = [...this.part().items].sort((a, b) => {
        const idxA = tokenOrderMap.get(a.id) ?? 9999;
        const idxB = tokenOrderMap.get(b.id) ?? 9999;
        return idxA - idxB;
      });
      const orderChanged = sortedItems.some((item, i) => item.id !== this.part().items[i]?.id);
      if (orderChanged) {
        this.part().items = sortedItems;
        itemsChanged = true;
      }

      if (itemsChanged) {
        this.part().items = [...this.part().items];
        this.syncCorrectToData();
      }

      if (textChanged || itemsChanged) {
        this.contentEditorService.notifyPartChanged();
        this.cdr.markForCheck();
      }
    }
    finally {
      this.isSyncingFromEditor = false;
    }
  }

  public syncFromEditor(): void {
    const tipTap = this.getTipTapEditor();
    if (!tipTap || this.isSyncingFromEditor || this.isSyncingFromItems) {
      return;
    }
    if (!this.isEditorInitialized) {
      const hasExistingTokens = (this.part().items?.length ?? 0) > 0 || (this.part().text?.includes('{id:') ?? false);
      if (tipTap.isEmpty && hasExistingTokens) {
        return;
      }
    }
    this.syncFromHtml(this.getEditorHtml(tipTap));
  }

  public onTextChange(content: string): void {
    if (this.isSyncingFromItems) {
      return;
    }
    const tipTap = this.getTipTapEditor();
    this.syncFromHtml(tipTap ? this.getEditorHtml(tipTap) : content);
  }

  public collapseAll(): void {
    this.expandedItems.clear();
  }

  public expandAll(): void {
    this.part().items.forEach(item => this.expandedItems.add(item.id));
  }

  public toggleItem(id: string): void {
    if (this.expandedItems.has(id)) {
      this.expandedItems.delete(id);
    }
    else {
      this.expandedItems.add(id);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private findTipTapTokenNode(id: string): { pos: number, node: any } | null {
    const tipTap = this.getTipTapEditor();
    if (!tipTap) return null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let match: { pos: number, node: any } | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tipTap.state.doc.descendants((node: any, pos: number) => {
      if (match) return false;
      if (this.adapter.isTokenNode(node)) {
        const token = this.adapter.extractTokenFromNode(node, pos);
        if (token && token.id === id) {
          match = { pos, node };
          return false;
        }
      }
      return true;
    });
    return match;
  }

  public onItemTextChange(item: TaschenrechnerItem, newText: string): void {
    item.text = newText;
    const items = this.part().items ?? [];
    const idx = items.findIndex(i => i.id === item.id);
    if (idx >= 0) {
      items[idx] = { ...items[idx], text: newText };
      this.part().items = [...items];
    }

    const tipTap = this.getTipTapEditor();
    if (tipTap) {
      const match = this.findTipTapTokenNode(item.id);
      if (match) {
        this.isSyncingFromItems = true;
        try {
          const updatedAttrs = this.adapter.updateNodeAttrs(match.node.attrs, newText);
          const tr = tipTap.state.tr.setNodeMarkup(match.pos, undefined, updatedAttrs);
          tipTap.view.dispatch(tr);
          this.part().text = this.adapter.fromHtmlToZav(this.getEditorHtml(tipTap));
        }
        finally {
          this.isSyncingFromItems = false;
        }
      }
      else {
        this.part().text = replaceTokenSolution(this.part().text, item.id, newText);
      }
    }
    else {
      this.part().text = replaceTokenSolution(this.part().text, item.id, newText);
    }

    const rule = this.getOrCreateCorrect(item.id, newText);
    rule.condition.right = newText;

    this.contentEditorService.notifyPartChanged();
    this.cdr.markForCheck();
  }

  public removeItem(index: number): void {
    const items = [...this.part().items];
    const removed = items.splice(index, 1);
    const item = removed[0];
    if (!item) return;

    const tipTap = this.getTipTapEditor();
    if (tipTap) {
      const match = this.findTipTapTokenNode(item.id);
      if (match) {
        const pos = match.pos;
        const nodeSize: number = typeof match.node.nodeSize === 'number' ? match.node.nodeSize : 1;
        const $pos = tipTap.state.doc.resolve(pos);
        const blockStart = $pos.start();
        const blockEnd = $pos.end();
        const beforeText = tipTap.state.doc.textBetween(blockStart, pos, ' ');
        const afterText = tipTap.state.doc.textBetween(pos + nodeSize, blockEnd, ' ');

        let replaceFrom: number = pos;
        let replaceTo: number = pos + nodeSize;
        let replacementText = item.text ?? '';

        const beforeTrim = beforeText.trimEnd();
        const afterTrim = afterText.trimStart();

        if (beforeTrim.endsWith('$$') && afterTrim.startsWith('$$')) {
          const dollarBeforeIdx = beforeText.lastIndexOf('$$');
          const dollarAfterIdx = afterText.indexOf('$$');
          replaceFrom = pos - (beforeText.length - dollarBeforeIdx);
          replaceTo = (pos + nodeSize) + (dollarAfterIdx + 2);
          replacementText = item.text ?? '';
        }
        else if (beforeTrim.endsWith('$$')) {
          const dollarBeforeIdx = beforeText.lastIndexOf('$$');
          replaceFrom = pos - (beforeText.length - dollarBeforeIdx);
          replacementText = `${item.text ?? ''}$$`;
        }
        else if (afterTrim.startsWith('$$')) {
          const dollarAfterIdx = afterText.indexOf('$$');
          replaceTo = (pos + nodeSize) + (dollarAfterIdx + 2);
          replacementText = `$$${item.text ?? ''}`;
        }

        this.isSyncingFromItems = true;
        try {
          if (replacementText) {
            const tr = tipTap.state.tr.replaceWith(
              replaceFrom,
              replaceTo,
              tipTap.state.schema.text(replacementText)
            );
            tipTap.view.dispatch(tr);
          }
          else {
            const tr = tipTap.state.tr.delete(replaceFrom, replaceTo);
            tipTap.view.dispatch(tr);
          }
          this.part().text = this.adapter.fromHtmlToZav(this.getEditorHtml(tipTap));
        }
        finally {
          this.isSyncingFromItems = false;
        }
      }
      else {
        this.part().text = removeTokenFromText(this.part().text, item.id);
      }
    }
    else {
      this.part().text = removeTokenFromText(this.part().text, item.id);
    }

    this.part().items = items;
    this.expandedItems.delete(item.id);

    // Remove from correct
    const correctIdx = this.correctList.findIndex(c => c.condition?.left === item.id);
    if (correctIdx >= 0) {
      this.correctList.splice(correctIdx, 1);
    }
    this.syncCorrectToData();

    // Remove conditional errors for this item
    const remainingErrors = this.conditionalErrorsList.filter(e => e.condition?.left !== item.id);
    this.conditionalErrorsList.length = 0;
    this.conditionalErrorsList.push(...remainingErrors);

    // Clean config entries
    delete this.mathEquality[item.id];
    delete this.ignoredConditions[item.id];

    this.contentEditorService.notifyPartChanged();
    this.cdr.markForCheck();
  }

  public onDrop(event: CdkDragDrop<TaschenrechnerItem[]>): void {
    const items = [...this.part().items];
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this.part().items = items;

    // Reorder correctList to match items order
    const correctMap = new Map<string, Correct>();
    for (const c of this.correctList) {
      if (c.condition?.left) {
        correctMap.set(c.condition.left, c);
      }
    }
    const reordered: Correct[] = items
      .map(it => correctMap.get(it.id))
      .filter((c): c is Correct => c !== undefined);
    this.correctList.length = 0;
    this.correctList.push(...reordered);
    this.syncCorrectToData();

    this.contentEditorService.notifyPartChanged();
    this.cdr.markForCheck();
  }

  public isMathSubject(): boolean {
    return this.subjectId() === '1';
  }

  public isMathEquality(itemId: string): boolean {
    return this.mathEquality[itemId] ?? false;
  }

  public setMathEquality(itemId: string, enabled: boolean): void {
    this.mathEquality[itemId] = enabled;
    this.contentEditorService.notifyPartChanged();
  }

  public canAutoCreateNumberList(text: string): boolean {
    return isNumberOrFraction(text);
  }

  public applyAutoNumberList(item: TaschenrechnerItem): void {
    const generated = createNumberListFrom(item.text);
    if (generated && generated !== item.text) {
      this.onItemTextChange(item, generated);
    }
  }

  public isIgnoredCondition(itemId: string): boolean {
    return this.ignoredConditions[itemId] ?? false;
  }

  public setIgnoredCondition(itemId: string, enabled: boolean): void {
    this.ignoredConditions[itemId] = enabled;
    this.contentEditorService.notifyPartChanged();
  }

  public getItemMessage(itemId: string): string {
    const rule = this.correctList.find(c => c.condition?.left === itemId);
    return rule?.message ?? '';
  }

  public setItemMessage(itemId: string, message: string): void {
    const item = this.part().items.find(i => i.id === itemId);
    const rule = this.getOrCreateCorrect(itemId, item?.text ?? '');
    rule.message = message;
    this.syncCorrectToData();
    this.contentEditorService.notifyPartChanged();
  }

  public getItemConditionalErrors(itemId: string): ConditionalError[] {
    return this.conditionalErrorsList.filter(e => e.condition?.left === itemId);
  }

  public addConditionalError(itemId: string): void {
    this.conditionalErrorsList.push({
      condition: {
        left: itemId,
        operation: 'in',
        right: '',
      },
      message: '',
    });
    this.contentEditorService.notifyPartChanged();
  }

  public removeConditionalError(error: ConditionalError): void {
    const idx = this.conditionalErrorsList.indexOf(error);
    if (idx >= 0) {
      this.conditionalErrorsList.splice(idx, 1);
      this.contentEditorService.notifyPartChanged();
    }
  }

  public onConditionalErrorRightChange(error: ConditionalError, right: string): void {
    error.condition.right = right;
    this.contentEditorService.notifyPartChanged();
  }

  public onConditionalErrorMessageChange(error: ConditionalError, message: string): void {
    error.message = message;
    this.contentEditorService.notifyPartChanged();
  }

  private getOrCreateCorrect(id: string, solution: string): Correct {
    let rule = this.correctList.find(c => c.condition?.left === id);
    if (!rule) {
      rule = {
        condition: {
          left: id,
          operation: 'in',
          right: solution,
        },
        message: '',
      };
      this.correctList.push(rule);
    }
    else {
      rule.condition.right = solution;
    }
    this.syncCorrectToData();
    return rule;
  }
}
